<template>
  <div class="space-y-6">
    <!-- Announcements Carousel -->
    <AnnouncementsCarousel />

    <!-- Open polls the user's unit can still vote on -->
    <OpenPollsCard />

    <!-- Resident: My HOA Dues and Open Invoice Cards -->
    <div
      v-if="isResident"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
    >
      <!-- My HOA Dues Card -->
      <div
        class="card card-hover cursor-pointer transition-all duration-200 ring-2 ring-primary"
        @click="navigateToInvoices"
      >
        <div class="flex items-center">
          <div class="p-3 bg-primary-100 rounded-lg">
            <svg
              class="w-6 h-6 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div class="ml-4 flex-1">
            <h3 class="text-sm font-medium text-gray-600">My HOA Dues</h3>
            <p class="text-2xl font-bold text-gray-900 mt-1">
              {{ activeInvoicesCount }}
              {{ activeInvoicesCount === 1 ? 'open invoice' : 'open invoices' }}
            </p>
            <p class="text-sm font-semibold text-gray-700 mt-0.5">
              {{ formatCurrency(activeInvoicesBalanceDue) }} due
            </p>
          </div>
        </div>
      </div>

      <!-- Open Invoice Cards (one per invoice) -->
      <div
        v-for="invoice in activeInvoices"
        :key="invoice.id"
        class="card card-hover cursor-pointer transition-all duration-200 ring-2 ring-primary flex flex-col"
        @click="navigateToInvoice(invoice.id)"
      >
        <div class="flex-1 flex flex-col">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-600">
                Invoice #{{ invoice.invoice_number }}
              </p>
              <p class="text-xl font-bold text-gray-900 mt-1">
                {{ formatCurrency(getInvoiceBalanceDue(invoice)) }}
              </p>
              <div
                class="mt-2 flex items-center gap-1.5"
                :class="
                  isInvoiceOverdue(invoice) ? 'text-red-600' : 'text-gray-600'
                "
              >
                <svg
                  v-if="isInvoiceOverdue(invoice)"
                  class="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span class="text-sm font-medium">
                  Due {{ getInvoiceDueDateDisplay(invoice) }}
                </span>
              </div>
            </div>
          </div>
          <div class="mt-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              class="btn-primary w-full text-sm py-2"
              @click.stop="navigateToInvoice(invoice.id)"
            >
              Pay Now
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Dashboard Stats (residents see Expenses and Budget only) -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <!-- Units and Residents Container - Stacked at lg, separate at md -->
      <!-- Hidden at md (Units and Residents will be individual grid items) -->
      <!-- Hidden for residents -->
      <div v-if="!isResident" class="hidden lg:flex lg:flex-col gap-6">
        <!-- Units -->
        <div class="card card-hover cursor-pointer" @click="navigateToUnits">
          <div class="flex items-center">
            <div class="p-3 bg-primary-100 rounded-lg">
              <svg
                class="w-6 h-6 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-sm font-medium text-gray-600">Units</h3>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ isLoading && !units ? '-' : activeUnitsCount }}
              </p>
            </div>
          </div>
        </div>

        <!-- Residents -->
        <div class="card card-hover cursor-pointer" @click="navigateToPeople">
          <div class="flex items-center">
            <div class="p-3 bg-primary-100 rounded-lg">
              <svg
                class="w-6 h-6 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-sm font-medium text-gray-600">Residents</h3>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ isLoading && !residents ? '-' : activeResidentsCount }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Units - Individual at mobile and md, hidden at lg -->
      <!-- Hidden for residents -->
      <div
        v-if="!isResident"
        class="card card-hover cursor-pointer block lg:hidden"
        @click="navigateToUnits"
      >
        <div class="flex items-center">
          <div class="p-3 bg-primary-100 rounded-lg">
            <svg
              class="w-6 h-6 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <div class="ml-4">
            <h3 class="text-sm font-medium text-gray-600">Units</h3>
            <p class="text-2xl font-bold text-gray-900 mt-1">
              {{ isLoading && !units ? '-' : activeUnitsCount }}
            </p>
          </div>
        </div>
      </div>

      <!-- Residents - Individual at mobile and md, hidden at lg -->
      <!-- Hidden for residents -->
      <div
        v-if="!isResident"
        class="card card-hover cursor-pointer block lg:hidden"
        @click="navigateToPeople"
      >
        <div class="flex items-center">
          <div class="p-3 bg-primary-100 rounded-lg">
            <svg
              class="w-6 h-6 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
          </div>
          <div class="ml-4">
            <h3 class="text-sm font-medium text-gray-600">Residents</h3>
            <p class="text-2xl font-bold text-gray-900 mt-1">
              {{ isLoading && !residents ? '-' : activeResidentsCount }}
            </p>
          </div>
        </div>
      </div>

      <!-- Combined Invoices & Payments Chart (admin only) -->
      <div
        v-if="!isResident"
        class="card card-hover cursor-pointer"
        @click="navigateToInvoices"
      >
        <div class="flex flex-col h-full">
          <div class="flex items-center mb-4">
            <div class="p-3 bg-primary-100 rounded-lg">
              <svg
                class="w-6 h-6 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div class="ml-4 flex-1">
              <h3 class="text-sm font-medium text-gray-600">
                Invoices & Payments (6M)
              </h3>
              <div class="flex items-baseline gap-3 mt-1">
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 bg-gray-900 rounded-full"></div>
                  <p class="text-lg font-bold text-gray-900">
                    {{ formatCurrency(activeInvoicesAmount) }}
                  </p>
                </div>
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <p class="text-lg font-bold text-gray-900">
                    {{ formatCurrency(totalCollectedAmount) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="isLoading && (!invoices || !payments)"
            class="relative w-full h-32 mt-2"
          >
            <!-- Loading grid lines -->
            <svg
              class="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
            >
              <line
                v-for="i in 5"
                :key="`combined-loading-grid-${i}`"
                x1="40"
                :y1="i * 20 + '%'"
                x2="100%"
                :y2="i * 20 + '%'"
                stroke="#F3F4F6"
                stroke-width="1"
              />
            </svg>
            <div
              class="flex items-end justify-center gap-1.5 h-full px-4 ml-10"
            >
              <div
                v-for="i in 6"
                :key="i"
                class="w-10 bg-gray-200 rounded-t flex-1"
                :style="`height: ${20 + Math.random() * 40}%`"
              ></div>
            </div>
          </div>
          <div v-else class="relative w-full h-32 mt-2">
            <!-- SVG for grid lines and axes -->
            <svg
              class="absolute inset-0 w-full"
              style="height: calc(100% - 1rem)"
              preserveAspectRatio="none"
            >
              <!-- Very soft grid lines -->
              <g class="grid-lines">
                <line
                  v-for="i in 5"
                  :key="`combined-grid-${i}`"
                  x1="40"
                  :y1="i * 20 + '%'"
                  x2="100%"
                  :y2="i * 20 + '%'"
                  stroke="#F3F4F6"
                  stroke-width="1"
                />
              </g>
              <!-- Y-axis line -->
              <line
                x1="40"
                y1="0"
                x2="40"
                y2="100%"
                stroke="#E5E7EB"
                stroke-width="1"
              />
              <!-- X-axis line - at the bottom of chart area -->
              <line
                x1="40"
                y1="100%"
                x2="100%"
                y2="100%"
                stroke="#E5E7EB"
                stroke-width="1"
              />
            </svg>

            <!-- Y-axis labels -->
            <div
              class="absolute left-0 top-0 w-10 flex flex-col justify-between pr-1"
              style="height: calc(100% - 1rem)"
            >
              <div
                v-for="(tick, index) in combinedYAxisTicks"
                :key="`combined-y-tick-${index}`"
                class="text-[10px] text-gray-500 text-right leading-tight"
              >
                {{ tick }}
              </div>
            </div>

            <!-- Chart area with side-by-side bars -->
            <div
              class="flex items-end justify-center gap-1 px-4 ml-10"
              style="height: calc(100% - 1rem)"
            >
              <div
                v-for="(monthData, index) in combinedChartData"
                :key="index"
                class="relative group flex-1 flex flex-col items-center gap-0.5"
                style="height: 100%"
              >
                <!-- Bar container - full height, bars aligned to bottom -->
                <div class="w-full flex items-end gap-0.5" style="height: 100%">
                  <!-- Invoice bar -->
                  <div
                    class="flex-1 bg-gray-900 rounded-t transition-all duration-300 hover:bg-gray-700"
                    :style="`height: ${getCombinedBarHeight(monthData.invoices)}%`"
                  ></div>
                  <!-- Payment bar -->
                  <div
                    class="flex-1 bg-blue-600 rounded-t transition-all duration-300 hover:bg-blue-700"
                    :style="`height: ${getCombinedBarHeight(monthData.payments)}%`"
                  ></div>
                </div>
                <!-- Tooltip -->
                <div
                  class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10"
                >
                  <div class="font-medium">{{ monthData.month }}</div>
                  <div class="mt-0.5 flex flex-col gap-0.5">
                    <div>
                      Invoices: {{ formatCurrency(monthData.invoices) }}
                    </div>
                    <div>
                      Payments: {{ formatCurrency(monthData.payments) }}
                    </div>
                  </div>
                  <!-- Tooltip arrow -->
                  <div
                    class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45"
                  ></div>
                </div>
              </div>
            </div>

            <!-- X-axis labels - positioned below the chart area -->
            <div
              class="flex justify-center gap-1 px-4 ml-10 mt-1"
              style="height: 1rem"
            >
              <div
                v-for="(monthData, index) in combinedChartData"
                :key="`combined-label-${index}`"
                class="flex-1 text-center"
              >
                <div
                  class="text-[10px] text-gray-500 leading-tight whitespace-nowrap"
                >
                  {{ monthData.month }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Expenses Pie Chart -->
      <div class="card card-hover cursor-pointer" @click="navigateToExpenses">
        <div class="flex flex-col h-full">
          <div class="flex items-center mb-4">
            <div class="p-3 bg-primary-100 rounded-lg">
              <svg
                class="w-6 h-6 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div class="ml-4 flex-1">
              <h3 class="text-sm font-medium text-gray-600">Expenses (6M)</h3>
              <p
                v-if="!isLoading"
                class="text-2xl font-bold text-gray-900 mt-1"
              >
                {{ formatCurrency(totalExpensesAmount) }}
              </p>
              <p v-else class="text-2xl font-bold text-gray-900 mt-1">-</p>
            </div>
          </div>
          <div
            v-if="isLoading && !expenses"
            class="relative w-full h-32 mt-2 flex items-center justify-center"
          >
            <div
              class="w-24 h-24 border-4 border-gray-200 border-t-gray-400 rounded-full animate-spin"
            ></div>
          </div>
          <div
            v-else-if="!expenses || expenses.length === 0"
            class="relative w-full h-32 mt-2 flex items-center justify-center"
          >
            <div class="text-center">
              <p class="text-sm text-gray-500">No expenses found</p>
            </div>
          </div>
          <div
            v-else-if="expenseCategoryData.length === 0"
            class="relative w-full h-32 mt-2 flex items-center justify-center"
          >
            <div class="text-center">
              <p class="text-sm text-gray-500">
                No expenses in the last 6 months
              </p>
            </div>
          </div>
          <div v-else class="relative w-full h-32 mt-2 flex items-center">
            <!-- Donut Chart SVG -->
            <svg
              class="w-32 h-32"
              viewBox="0 0 100 100"
              style="transform: rotate(-90deg)"
            >
              <g>
                <path
                  v-for="slice in pieChartSlices"
                  :key="slice.categoryId"
                  :d="
                    getDonutSlicePath(
                      slice.startAngle,
                      slice.endAngle,
                      40,
                      20,
                      50,
                      50
                    )
                  "
                  :fill="getExpenseCategoryColor(slice.categoryId)"
                  class="transition-opacity duration-200 hover:opacity-80 cursor-pointer"
                  :data-category-id="slice.categoryId"
                />
              </g>
            </svg>
            <!-- Legend -->
            <div class="flex-1 ml-4 space-y-1">
              <div
                v-for="item in expenseCategoryData"
                :key="item.categoryId"
                class="flex items-center gap-2 text-xs group"
              >
                <div
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="`background-color: ${getExpenseCategoryColor(item.categoryId)}`"
                ></div>
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 truncate">
                    {{ item.displayName }}
                  </div>
                  <div class="text-gray-500">
                    {{ formatCurrency(item.amount) }} ({{
                      item.percentage.toFixed(1)
                    }}%)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Budget Summary Card -->
      <div class="card card-hover cursor-pointer" @click="navigateToBudget">
        <div class="flex flex-col h-full">
          <div class="flex items-center mb-4">
            <div class="p-3 bg-purple-100 rounded-lg">
              <svg
                class="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div class="ml-4 flex-1">
              <h3 class="text-sm font-medium text-gray-600">
                Budget ({{ currentYear }})
              </h3>
              <div v-if="!budgetLoading && budgetData" class="mt-2 space-y-1">
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Forecast</span>
                  <span class="text-sm font-semibold text-gray-900">
                    {{ formatCurrency(budgetForecast) }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">Actual</span>
                  <span
                    class="text-sm font-semibold"
                    :class="
                      budgetActual >= budgetForecast
                        ? 'text-green-600'
                        : 'text-red-600'
                    "
                  >
                    {{ formatCurrency(budgetActual) }}
                  </span>
                </div>
              </div>
              <p v-else class="text-2xl font-bold text-gray-900 mt-1">-</p>
            </div>
          </div>
        </div>
      </div>

      <!-- HOA Account Balance Card (hidden for residents) -->
      <div
        v-if="!isResident"
        class="card card-hover cursor-pointer"
        @click="navigateToTransactions"
      >
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-sm font-medium text-gray-600">
              HOA Account Balance
            </h3>
            <p class="text-2xl font-bold text-gray-900 mt-1">
              {{ formatCurrency(totalAccountBalance) }}
            </p>
          </div>
          <div class="p-3 bg-primary-100 rounded-lg">
            <svg
              class="w-6 h-6 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions (Hidden for residents) -->
    <div v-if="!isResident" class="card-modern">
      <h3 class="text-base font-semibold text-gray-900 mb-4">Quick Actions</h3>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <router-link to="/invoices/create" class="block">
          <div
            class="p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary-50 transition-colors duration-200 text-center"
          >
            <svg
              class="w-8 h-8 text-primary mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p class="text-sm font-medium text-gray-900">New Invoice</p>
          </div>
        </router-link>

        <router-link to="/people/add" class="block">
          <div
            class="p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary-50 transition-colors duration-200 text-center"
          >
            <svg
              class="w-8 h-8 text-primary mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <p class="text-sm font-medium text-gray-900">Add Resident</p>
          </div>
        </router-link>

        <router-link to="/units/add" class="block">
          <div
            class="p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary-50 transition-colors duration-200 text-center"
          >
            <svg
              class="w-8 h-8 text-primary mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <p class="text-sm font-medium text-gray-900">Add Unit</p>
          </div>
        </router-link>

        <router-link to="/expenses/add" class="block">
          <div
            class="p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary-50 transition-colors duration-200 text-center"
          >
            <svg
              class="w-8 h-8 text-primary mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p class="text-sm font-medium text-gray-900">Add Expense</p>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="card-modern">
      <h3 class="text-base font-semibold text-gray-900 mb-4">
        Recent Activity
      </h3>
      <div class="text-center py-8">
        <svg
          class="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">
          No Recent Activity
        </h3>
        <p class="mt-1 text-sm text-gray-500">
          Activity feed will be displayed here
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useUnits } from '../composables/useUnits';
import { useInvoices } from '../composables/useInvoices';
import { usePayments } from '../composables/usePayments';
import { useResidents } from '../composables/useResidents';
import { useExpenses } from '../composables/useExpenses';
import { useBudget } from '../composables/useBudget';
import { useAuthStore } from '../stores/auth';
import { useBankAccounts, type BankAccount } from '@neibrpay/api-client';
import AnnouncementsCarousel from '../components/AnnouncementsCarousel.vue';
import OpenPollsCard from '../components/OpenPollsCard.vue';
import type {
  Unit,
  InvoiceUnit,
  Payment,
  Resident,
  Expense,
} from '@neibrpay/models';
// Removed ExpenseCategory import - using budget_category instead

const router = useRouter();
const authStore = useAuthStore();

// Role check
const isResident = computed(() => authStore.isResident);

// Query hooks
const { data: units, isLoading: unitsLoading } = useUnits(false);
const { data: invoices, isLoading: invoicesLoading } = useInvoices();
const { data: payments, isLoading: paymentsLoading } = usePayments();
const { data: residents, isLoading: residentsLoading } = useResidents(false);
const { data: expenses, isLoading: expensesLoading } = useExpenses();
const { data: bankAccountsData } = useBankAccounts();
const bankAccounts = computed(
  () => bankAccountsData.value?.bank_accounts || []
);

// Budget data for current year
const currentYear = new Date().getFullYear();
const { data: budgetData, isLoading: budgetLoading } = useBudget(currentYear);

// Currency formatting helper
const formatCurrency = (amount: number): string => {
  // Handle NaN, null, or undefined
  const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safeAmount);
};

// Format number for y-axis: "1.4k", "14k", "1.2M" (max 3-4 chars)
const formatCompactNumber = (value: number): string => {
  if (value === 0) return '0';
  const absValue = Math.abs(value);

  if (absValue >= 1000000) {
    const millions = absValue / 1000000;
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    const thousands = absValue / 1000;
    return thousands % 1 === 0 ? `${thousands}k` : `${thousands.toFixed(1)}k`;
  }
  return absValue.toString();
};

// Computed statistics
const activeUnitsCount = computed(() => {
  if (!units.value) return 0;
  return units.value.filter(
    (unit: Unit) => unit.is_active === true && !unit.deleted_at
  ).length;
});

const activeInvoices = computed(() => {
  if (!invoices.value) return [];
  return invoices.value.filter(
    (invoice: InvoiceUnit) =>
      (invoice.status === 'sent' || invoice.status === 'partial') &&
      !invoice.deleted_at
  );
});

const activeInvoicesCount = computed(() => {
  return activeInvoices.value.length;
});

const activeInvoicesAmount = computed(() => {
  if (!activeInvoices.value || activeInvoices.value.length === 0) return 0;

  const total = activeInvoices.value.reduce(
    (sum: number, invoice: InvoiceUnit) => {
      // Get the total value - could be number, string, or null/undefined
      const invoiceTotal = invoice?.total;

      // Convert to number - handle runtime string values from API
      let amount = 0;
      if (invoiceTotal != null && invoiceTotal !== undefined) {
        // API might return as string, so convert if needed
        const numValue =
          typeof invoiceTotal === 'string'
            ? parseFloat(invoiceTotal)
            : invoiceTotal;
        amount =
          typeof numValue === 'number' && !isNaN(numValue) ? numValue : 0;
      }

      return sum + amount;
    },
    0
  );

  return total;
});

// Total balance due across active invoices (for Resident "My HOA Dues" card)
const activeInvoicesBalanceDue = computed(() => {
  if (!activeInvoices.value || activeInvoices.value.length === 0) return 0;
  return activeInvoices.value.reduce((sum: number, invoice: InvoiceUnit) => {
    const balance =
      typeof invoice.balance_due === 'number' && !isNaN(invoice.balance_due)
        ? invoice.balance_due
        : typeof invoice.balance_due === 'string'
          ? parseFloat(invoice.balance_due) || 0
          : 0;
    return sum + balance;
  }, 0);
});

// Format date for display (e.g. "Mar 13, 2025")
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatCalendarDate = (ymd: string): string => {
  const parts = ymd.split('-').map(p => parseInt(p, 10));
  if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Get due date display string for an invoice (mirrors InvoiceDetail getDueDate logic)
const getInvoiceDueDateDisplay = (invoice: InvoiceUnit): string => {
  if (!invoice?.start_date) return 'N/A';
  if (invoice.due_calendar_date) {
    return formatCalendarDate(invoice.due_calendar_date);
  }
  const startDate = new Date(invoice.start_date);
  let dueDate = new Date(startDate);
  const dueDateEnum = invoice.due_date || 'use_payment_terms';

  switch (dueDateEnum) {
    case 'net_15':
      dueDate.setDate(startDate.getDate() + 15);
      break;
    case 'net_30':
      dueDate.setDate(startDate.getDate() + 30);
      break;
    case 'net_45':
      dueDate.setDate(startDate.getDate() + 45);
      break;
    case 'net_60':
      dueDate.setDate(startDate.getDate() + 60);
      break;
    case 'due_on_receipt':
      return 'on Receipt';
    case 'use_payment_terms':
    default:
      return '—';
  }
  return formatDate(dueDate.toISOString());
};

// Check if invoice due date is in the past (for overdue styling)
const isInvoiceOverdue = (invoice: InvoiceUnit): boolean => {
  if (
    !invoice?.start_date ||
    invoice.status === 'paid' ||
    invoice.status === 'cancelled'
  ) {
    return false;
  }
  if (invoice.days_until_due !== null && invoice.days_until_due !== undefined) {
    return invoice.days_until_due < 0;
  }
  const dueDateEnum = invoice.due_date || 'use_payment_terms';
  if (dueDateEnum === 'due_on_receipt' || dueDateEnum === 'use_payment_terms') {
    return false;
  }
  const startDate = new Date(invoice.start_date);
  const dueDate = new Date(startDate);
  switch (dueDateEnum) {
    case 'net_15':
      dueDate.setDate(startDate.getDate() + 15);
      break;
    case 'net_30':
      dueDate.setDate(startDate.getDate() + 30);
      break;
    case 'net_45':
      dueDate.setDate(startDate.getDate() + 45);
      break;
    case 'net_60':
      dueDate.setDate(startDate.getDate() + 60);
      break;
    default:
      return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < today;
};

// Get balance due for display (handles number or string from API)
const getInvoiceBalanceDue = (invoice: InvoiceUnit): number => {
  if (invoice.balance_due == null) return 0;
  const n =
    typeof invoice.balance_due === 'string'
      ? parseFloat(invoice.balance_due)
      : invoice.balance_due;
  return typeof n === 'number' && !isNaN(n) ? n : 0;
};

const navigateToInvoice = (id: number) => {
  router.push(`/invoices/${id}`);
};

const activeResidentsCount = computed(() => {
  if (!residents.value) return 0;
  return residents.value.filter(
    (resident: Resident) => resident.is_active === true && !resident.deleted_at
  ).length;
});

// Process active invoices for last 6 months chart
const monthlyInvoiceData = computed(() => {
  if (!activeInvoices.value || activeInvoices.value.length === 0) {
    // Return empty data for last 6 months
    const months: string[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toLocaleDateString('en-US', { month: 'short' }));
    }
    return months.map(month => ({
      month,
      Amount: 0,
    }));
  }

  // Get last 6 months
  const monthlyTotals: Record<string, number> = {};
  const now = new Date();

  // Initialize all months with 0
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
    monthlyTotals[monthKey] = 0;
  }

  // Group active invoices by month based on created_at
  activeInvoices.value.forEach((invoice: InvoiceUnit) => {
    if (!invoice.created_at) return;

    const invoiceDate = new Date(invoice.created_at);
    const monthKey = invoiceDate.toLocaleDateString('en-US', {
      month: 'short',
    });
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);

    // Only include invoices from last 6 months
    if (invoiceDate >= sixMonthsAgo) {
      if (monthlyTotals[monthKey] !== undefined) {
        // Handle total as number or string
        const amount =
          typeof invoice.total === 'string'
            ? parseFloat(invoice.total) || 0
            : invoice.total || 0;
        monthlyTotals[monthKey] += amount;
      }
    }
  });

  // Convert to array format for chart
  const months: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(date.toLocaleDateString('en-US', { month: 'short' }));
  }

  return months.map(month => ({
    month,
    Amount: monthlyTotals[month] || 0,
  }));
});

// Process payments for last 6 months chart
const monthlyPaymentData = computed(() => {
  if (!payments.value || payments.value.length === 0) {
    // Return empty data for last 6 months
    const months: string[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toLocaleDateString('en-US', { month: 'short' }));
    }
    return months.map(month => ({
      month,
      Collections: 0,
    }));
  }

  // Get last 6 months
  const monthlyTotals: Record<string, number> = {};
  const now = new Date();

  // Initialize all months with 0
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
    monthlyTotals[monthKey] = 0;
  }

  // Group payments by month
  payments.value.forEach((payment: Payment) => {
    const paymentDate = new Date(payment.payment_date);
    const monthKey = paymentDate.toLocaleDateString('en-US', {
      month: 'short',
    });
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);

    // Only include payments from last 6 months
    if (paymentDate >= sixMonthsAgo) {
      if (monthlyTotals[monthKey] !== undefined) {
        // Handle amount as number or string
        const amount =
          typeof payment.amount === 'string'
            ? parseFloat(payment.amount) || 0
            : payment.amount || 0;
        monthlyTotals[monthKey] += amount;
      }
    }
  });

  // Convert to array format for chart
  const months: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(date.toLocaleDateString('en-US', { month: 'short' }));
  }

  return months.map(month => ({
    month,
    Collections: monthlyTotals[month] || 0,
  }));
});

// Calculate total collected amount for the 6 months
const totalCollectedAmount = computed(() => {
  if (!monthlyPaymentData.value) return 0;
  return monthlyPaymentData.value.reduce(
    (sum: number, monthData: { month: string; Collections: number }) =>
      sum + (monthData.Collections || 0),
    0
  );
});

// Calculate Y-axis ticks for grid lines (formatted as compact numbers) - Payments
const yAxisTicks = computed(() => {
  if (!monthlyPaymentData.value || monthlyPaymentData.value.length === 0)
    return ['0', '0', '0', '0', '0'];

  const maxValue = Math.max(
    ...monthlyPaymentData.value.map(
      (m: { month: string; Collections: number }) => m.Collections || 0
    ),
    1
  );

  if (maxValue === 0) return ['0', '0', '0', '0', '0'];

  // Generate 5 ticks from 0 to max
  const ticks: string[] = [];
  for (let i = 4; i >= 0; i--) {
    const tickValue = (maxValue / 4) * i;
    ticks.push(formatCompactNumber(tickValue));
  }
  return ticks;
});

// Calculate bar height as percentage (0-100%) - Payments
const getBarHeight = (value: number): number => {
  if (!monthlyPaymentData.value || monthlyPaymentData.value.length === 0)
    return 0;
  const maxValue = Math.max(
    ...monthlyPaymentData.value.map(
      (m: { month: string; Collections: number }) => m.Collections || 0
    ),
    1 // Prevent division by zero
  );
  if (maxValue === 0) return 0;
  if (value === 0) return 0;
  // Return as percentage (0-100%) - allow 0 height for zero values
  return (value / maxValue) * 100;
};

// Calculate Y-axis ticks for invoice chart (formatted as compact numbers)
const invoiceYAxisTicks = computed(() => {
  if (!monthlyInvoiceData.value || monthlyInvoiceData.value.length === 0)
    return ['0', '0', '0', '0', '0'];

  const maxValue = Math.max(
    ...monthlyInvoiceData.value.map(
      (m: { month: string; Amount: number }) => m.Amount || 0
    ),
    1
  );

  if (maxValue === 0) return ['0', '0', '0', '0', '0'];

  // Generate 5 ticks from 0 to max
  const ticks: string[] = [];
  for (let i = 4; i >= 0; i--) {
    const tickValue = (maxValue / 4) * i;
    ticks.push(formatCompactNumber(tickValue));
  }
  return ticks;
});

// Calculate bar height as percentage (0-100%) - Invoices
const getInvoiceBarHeight = (value: number): number => {
  if (!monthlyInvoiceData.value || monthlyInvoiceData.value.length === 0)
    return 0;
  const maxValue = Math.max(
    ...monthlyInvoiceData.value.map(
      (m: { month: string; Amount: number }) => m.Amount || 0
    ),
    1 // Prevent division by zero
  );
  if (maxValue === 0) return 0;
  if (value === 0) return 0;
  // Return as percentage (0-100%) - allow 0 height for zero values
  return (value / maxValue) * 100;
};

// Combined chart data - merge invoices and payments
const combinedChartData = computed(() => {
  const invoiceData = monthlyInvoiceData.value;
  const paymentData = monthlyPaymentData.value;

  if (!invoiceData || !paymentData) return [];

  return invoiceData.map((invoiceMonth, index) => ({
    month: invoiceMonth.month,
    invoices: invoiceMonth.Amount,
    payments: paymentData[index]?.Collections || 0,
  }));
});

// Unified Y-axis ticks for combined chart
const combinedYAxisTicks = computed(() => {
  const data = combinedChartData.value;
  if (!data || data.length === 0) return ['0', '0', '0', '0', '0'];

  const maxValue = Math.max(
    ...data.map(d => Math.max(d.invoices || 0, d.payments || 0)),
    1
  );

  if (maxValue === 0) return ['0', '0', '0', '0', '0'];

  const ticks: string[] = [];
  for (let i = 4; i >= 0; i--) {
    const tickValue = (maxValue / 4) * i;
    ticks.push(formatCompactNumber(tickValue));
  }
  return ticks;
});

// Get bar height for combined chart
const getCombinedBarHeight = (value: number): number => {
  const data = combinedChartData.value;
  if (!data || data.length === 0) return 0;

  const maxValue = Math.max(
    ...data.map(d => Math.max(d.invoices || 0, d.payments || 0)),
    1
  );

  if (maxValue === 0) return 0;
  if (value === 0) return 0;
  return (value / maxValue) * 100;
};

// Expense category data for pie chart (last 6 months)
const expenseCategoryData = computed(() => {
  if (!expenses.value || expenses.value.length === 0) return [];

  const now = new Date();
  // Set to first day of the month 6 months ago
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  sixMonthsAgo.setHours(0, 0, 0, 0);

  // Filter expenses from last 6 months
  const recentExpenses = expenses.value.filter((expense: Expense) => {
    // Skip deleted expenses
    if (expense.deleted_at) return false;

    // Use invoice_date as primary, fallback to created_at
    const dateString = expense.invoice_date || expense.created_at;
    if (!dateString) return false;

    try {
      const expenseDate = new Date(dateString);
      expenseDate.setHours(0, 0, 0, 0);
      // Include expenses from the start of 6 months ago to now
      return expenseDate >= sixMonthsAgo && expenseDate <= now;
    } catch (e) {
      // If date parsing fails, exclude the expense
      return false;
    }
  });

  // Group by budget category and sum amounts
  const categoryTotals: Record<number, { amount: number; name: string }> = {};
  let totalAmount = 0;

  recentExpenses.forEach((expense: Expense) => {
    // Skip expenses without a budget category
    if (!expense.budget_category_id || !expense.budget_category) return;

    // Handle amount conversion (could be string from API)
    let amount = 0;
    if (expense.invoice_amount != null) {
      amount =
        typeof expense.invoice_amount === 'string'
          ? parseFloat(expense.invoice_amount) || 0
          : expense.invoice_amount || 0;
    }

    // Only include expenses with positive amounts
    if (amount > 0) {
      const categoryId = expense.budget_category_id;
      if (!categoryTotals[categoryId]) {
        categoryTotals[categoryId] = {
          amount: 0,
          name: expense.budget_category.name || 'Uncategorized',
        };
      }
      categoryTotals[categoryId].amount += amount;
      totalAmount += amount;
    }
  });

  // If no expenses with amounts > 0, return empty array
  if (totalAmount === 0) return [];

  // Convert to array with percentages - only include categories with amounts > 0
  const result = Object.keys(categoryTotals)
    .map(key => Number(key))
    .filter(categoryId => categoryTotals[categoryId].amount > 0)
    .map(categoryId => {
      const categoryData = categoryTotals[categoryId];
      const amount = categoryData.amount;
      const percentage = (amount / totalAmount) * 100;

      return {
        categoryId,
        displayName: categoryData.name,
        amount,
        percentage,
      };
    })
    .sort((a, b) => b.amount - a.amount);

  return result;
});

// Total expenses amount for last 6 months
const totalExpensesAmount = computed(() => {
  if (!expenseCategoryData.value || expenseCategoryData.value.length === 0)
    return 0;
  return expenseCategoryData.value.reduce((sum, item) => sum + item.amount, 0);
});

// Color palette for expense categories (based on category ID)
// Uses a hash function to generate consistent colors for each category
const getExpenseCategoryColor = (categoryId: number): string => {
  // Color palette for budget categories
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#EF4444', // red
    '#F59E0B', // yellow/amber
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#14B8A6', // teal
    '#F97316', // orange
    '#6366F1', // indigo
    '#84CC16', // lime
  ];

  // Use category ID to consistently map to a color
  return colors[categoryId % colors.length] || '#6B7280';
};

// Pie chart slices with calculated angles
const pieChartSlices = computed(() => {
  if (!expenseCategoryData.value || expenseCategoryData.value.length === 0)
    return [];

  let currentAngle = 0;
  return expenseCategoryData.value.map(item => {
    const startAngle = currentAngle;
    const angle = (item.percentage / 100) * 360;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    return {
      ...item,
      startAngle,
      endAngle,
    };
  });
});

// Calculate donut chart path for SVG
const getDonutSlicePath = (
  startAngle: number,
  endAngle: number,
  outerRadius: number,
  innerRadius: number,
  centerX: number,
  centerY: number
): string => {
  const angleDiff = endAngle - startAngle;
  const isFullCircle = angleDiff >= 359.99 || Math.abs(angleDiff - 360) < 0.01;

  // For full circle (360 degrees), draw as two 180-degree arcs
  if (isFullCircle) {
    const midAngle = startAngle + 180;

    // Outer ring points
    const startOuter = polarToCartesian(
      centerX,
      centerY,
      outerRadius,
      startAngle
    );
    const midOuter = polarToCartesian(centerX, centerY, outerRadius, midAngle);
    const endOuter = polarToCartesian(centerX, centerY, outerRadius, endAngle);

    // Inner ring points (reverse order)
    const startInner = polarToCartesian(
      centerX,
      centerY,
      innerRadius,
      endAngle
    );
    const midInner = polarToCartesian(centerX, centerY, innerRadius, midAngle);
    const endInner = polarToCartesian(
      centerX,
      centerY,
      innerRadius,
      startAngle
    );

    // Draw complete donut: outer arc (180°) -> line to inner -> inner arc (180° reverse) -> close
    return [
      'M',
      startOuter.x,
      startOuter.y,
      'A',
      outerRadius,
      outerRadius,
      0,
      1,
      1, // large arc, clockwise
      midOuter.x,
      midOuter.y,
      'A',
      outerRadius,
      outerRadius,
      0,
      1,
      1, // large arc, clockwise
      endOuter.x,
      endOuter.y,
      'L',
      startInner.x,
      startInner.y,
      'A',
      innerRadius,
      innerRadius,
      0,
      1,
      0, // large arc, counter-clockwise
      midInner.x,
      midInner.y,
      'A',
      innerRadius,
      innerRadius,
      0,
      1,
      0, // large arc, counter-clockwise
      endInner.x,
      endInner.y,
      'Z',
    ].join(' ');
  }

  // For partial arcs, use standard donut path
  const startOuter = polarToCartesian(
    centerX,
    centerY,
    outerRadius,
    startAngle
  );
  const endOuter = polarToCartesian(centerX, centerY, outerRadius, endAngle);
  const startInner = polarToCartesian(centerX, centerY, innerRadius, endAngle);
  const endInner = polarToCartesian(centerX, centerY, innerRadius, startAngle);

  const largeArcFlag = angleDiff > 180 ? 1 : 0;

  return [
    'M',
    startOuter.x,
    startOuter.y,
    'A',
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    1,
    endOuter.x,
    endOuter.y,
    'L',
    startInner.x,
    startInner.y,
    'A',
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    0,
    endInner.x,
    endInner.y,
    'Z',
  ].join(' ');
};

// Helper function to convert polar coordinates to cartesian
const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

// Navigation handlers
const navigateToUnits = () => {
  router.push('/units');
};

const navigateToInvoices = () => {
  router.push('/invoices');
};

const navigateToPayments = () => {
  router.push('/payments');
};

const navigateToExpenses = () => {
  router.push('/expenses');
};

const navigateToPeople = () => {
  router.push('/people');
};

const navigateToBudget = () => {
  router.push('/budget');
};

const navigateToTransactions = () => {
  router.push('/transactions');
};

// Calculate total account balance
const totalAccountBalance = computed(() => {
  return bankAccounts.value.reduce((sum: number, account: BankAccount) => {
    return sum + Number(account.current_balance || 0);
  }, 0);
});

// Budget summary calculations
const budgetForecast = computed(() => {
  if (!budgetData.value) return 0;
  const incomeForecast = budgetData.value.income.reduce(
    (sum, cat) => sum + cat.total.forecast,
    0
  );
  const expenseForecast = budgetData.value.expense.reduce(
    (sum, cat) => sum + cat.total.forecast,
    0
  );
  return incomeForecast - expenseForecast;
});

const budgetActual = computed(() => {
  if (!budgetData.value) return 0;
  const incomeActual = budgetData.value.income.reduce(
    (sum, cat) => sum + cat.total.actual,
    0
  );
  const expenseActual = budgetData.value.expense.reduce(
    (sum, cat) => sum + cat.total.actual,
    0
  );
  return incomeActual - expenseActual;
});

// Loading state
const isLoading = computed(() => {
  return (
    unitsLoading.value ||
    invoicesLoading.value ||
    paymentsLoading.value ||
    residentsLoading.value ||
    expensesLoading.value
  );
});
</script>
